/**
 * SubmitDocumentsFlow — wraps roadmap, chat, and summary.
 * Fetches chat-flow to show 各種コピー progress (X/Y) and to decide: open chat or summary.
 * - If 各種コピー not yet complete: open chat (resume where they left off).
 * - If 各種コピー complete: open document summary screen. Continue from summary → back to roadmap (5/5, green).
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import {
  fetchChatFlow,
  postAckChatFlowNotice,
  postCompleteFormToFill,
  postMarkDraft,
  postMarkSubmitted,
  postReopenFormToFill,
  postStartFormToFill,
  type ChatFlowPendingNotice,
  type ChatFlowProfileSectionItem,
  type CareerUpPathValue,
} from '../../../services/chatFlowApi';
import { API_BASE_URL } from '../../../config';
import { getEmployeeEmail } from '../auth/employeeAuthStorage';
import DocumentChecklistScreen from './checklist/DocumentChecklistScreen';
import InvitationUpdateNoticeModal from './InvitationUpdateNoticeModal';
import ChatDocumentFlow from './chat/ChatDocumentFlow';
import DocumentSummaryScreen from './summary/DocumentSummaryScreen';
import EditDocumentChatFlow from './chat/EditDocumentChatFlow';
import FillingFormsIntroChatFlow from '../fillingForms/chat/FillingFormsIntroChatFlow';
import GeneralProfileChatFlow, { type GeneralProfileNamePayload } from '../fillingForms/chat/GeneralProfile/GeneralProfileChatFlow';
import FamilyInfoChatFlow from '../fillingForms/chat/family/FamilyInfoChatFlow';
import WorkerRegistryChatFlow from '../fillingForms/chat/workerRegistry/WorkerRegistryChatFlow';
import SkipFillInFormChatFlow from '../fillingForms/chat/skipFillIn/SkipFillInFormChatFlow';
import EmploymentContractChatFlow from '../fillingForms/chat/employmentContract/EmploymentContractChatFlow';
import HireHealthSurveyChatFlow from '../fillingForms/chat/hireHealthSurvey/HireHealthSurveyChatFlow';
import BankAccountDetailsChatFlow from '../fillingForms/chat/bankAccountDetails/BankAccountDetailsChatFlow';
import DependentDeductionChatFlow from '../fillingForms/chat/dependentDeduction/DependentDeductionChatFlow';
import CareerHistorySurveyChatFlow from '../fillingForms/chat/careerHistorySurvey/CareerHistorySurveyChatFlow';
import PreEmploymentEducationChatFlow from '../fillingForms/chat/preEmploymentEducation/PreEmploymentEducationChatFlow';
import SiteArrivalConfirmationChatFlow from '../fillingForms/chat/siteArrivalConfirmation/SiteArrivalConfirmationChatFlow';
import CareerUpConfirmationChatFlow from '../fillingForms/chat/careerUpConfirmation/CareerUpConfirmationChatFlow';
import CareerUpApplicationChatFlow from '../fillingForms/chat/careerUpApplication/CareerUpApplicationChatFlow';
import HealthInfoZuidoChatFlow from '../fillingForms/chat/healthInfoZuido/HealthInfoZuidoChatFlow';
import { CAREER_UP_CONSENT_PAGE_COUNT } from '../fillingForms/chat/careerUpApplication/careerUpApplicationTypes';
import { HEALTH_INFO_ZUIDO_CONSENT_PAGE_COUNT, HEALTH_INFO_ZUIDO_FILLED_PREVIEW_PAGE_COUNT } from '../fillingForms/chat/healthInfoZuido/healthInfoZuidoTypes';
import PdfPreviewReviewScreen from '../documents/pdfPreview/PdfPreviewReviewScreen';
import {
  fetchFillInPdfPreviewBlob,
  type FillInPdfPreviewRequest,
} from '../documents/pdfPreview/fillInPdfPreviewModel';
import FillingFormsChecklistScreen from '../fillingForms/checklist/FillingFormsChecklistScreen';
import SignatureCaptureScreen from '../fillingForms/signature/SignatureCaptureScreen';
import { isSignatureCaptured } from '../fillingForms/signature/signatureApi';
import { resolveFillInFormFlowId } from '../fillingForms/fillInFormRoutes';
import { isConfirmationFormCode, partitionFormsByCategory } from '../fillingForms/formDocumentCategory';
import type { ChatFlowDocumentItem } from '../../../services/chatFlowApi';
import type { FillingFormsChecklistForm } from '../fillingForms/checklist/FillingFormsChecklistScreen';
import type { FillingFormProgressStatus, FillingFormsChecklistItem } from '../fillingForms/checklist/types';

export interface SubmitDocumentsFlowProps {
  onBackToMenu: () => void;
  /** submit = initial submission flow; edit = resubmission/edit flow from menu */
  mode?: 'submit' | 'edit';
}

type FormsChecklistPhase = 'fillingFormsChecklist' | 'confirmationFormsChecklist';

type Phase =
  | 'roadmap'
  | 'chat'
  | 'summary'
  | 'edit'
  | 'fillingFormsIntro'
  | 'fillingFormsChecklist'
  | 'confirmationFormsChecklist'
  | 'fillingGeneralProfileName'
  | 'signatureCapture'
  | 'fillingFamilyInfo'
  | 'workerRegistryChat'
  | 'skipFillInChat'
  | 'employmentContractChat'
  | 'hireHealthSurveyChat'
  | 'bankAccountDetailsChat'
  | 'dependentDeductionChat'
  | 'careerHistorySurveyChat'
  | 'preEmploymentEducationChat'
  | 'siteArrivalConfirmationChat'
  | 'careerUpConfirmationChat'
  | 'careerUpApplicationChat'
  | 'healthInfoZuidoChat'
  | 'pdfPreviewReview';

function isPagedConsentPreviewSession(session: {
  request: { kind: FillInPdfPreviewRequest['kind'] };
  pagedTotalPages?: number;
} | null): boolean {
  if (!session || typeof session.pagedTotalPages !== 'number' || session.pagedTotalPages <= 1) {
    return false;
  }
  return (
    session.request.kind === 'career-up-application' || session.request.kind === 'health-info-zuido'
  );
}

function pagedPreviewPageTitleKey(
  kind: FillInPdfPreviewRequest['kind'],
  page: number
): string | null {
  if (page < 1 || page > 3) return null;
  if (kind === 'career-up-application') {
    return `submitDocuments.careerUpApplication.page${page}Title`;
  }
  if (kind === 'health-info-zuido') {
    return `submitDocuments.healthInfoZuido.page${page}Title`;
  }
  return null;
}

function pagedPreviewReadHelperKey(kind: FillInPdfPreviewRequest['kind'], filledPreview?: boolean): string {
  if (kind === 'health-info-zuido' && filledPreview) {
    return 'submitDocuments.healthInfoZuido.filledPreviewHelper';
  }
  if (kind === 'health-info-zuido') {
    return 'submitDocuments.healthInfoZuido.readHelper';
  }
  return 'submitDocuments.careerUpApplication.readHelper';
}

function pagedPreviewReadConfirmKey(kind: FillInPdfPreviewRequest['kind']): string {
  if (kind === 'health-info-zuido') {
    return 'submitDocuments.healthInfoZuido.readConfirm';
  }
  return 'submitDocuments.careerUpApplication.readConfirm';
}

function pagedPreviewPageIndicatorKey(kind: FillInPdfPreviewRequest['kind']): string {
  if (kind === 'health-info-zuido') {
    return 'submitDocuments.healthInfoZuido.pageIndicator';
  }
  return 'submitDocuments.careerUpApplication.pageIndicator';
}

function pagedPreviewNextPageKey(kind: FillInPdfPreviewRequest['kind']): string {
  if (kind === 'health-info-zuido') {
    return 'submitDocuments.healthInfoZuido.nextPage';
  }
  return 'submitDocuments.careerUpApplication.nextPage';
}

function pagedPreviewPrevPageKey(kind: FillInPdfPreviewRequest['kind']): string {
  if (kind === 'health-info-zuido') {
    return 'submitDocuments.healthInfoZuido.prevPage';
  }
  return 'submitDocuments.careerUpApplication.prevPage';
}

/** Count documents that have a choice (submitted, send_later, bring_when_boarding, or skipped for no-card) */
function countVariousCopiesCompleted(documents: { status: string }[]): number {
  return documents.filter(
    (d) =>
      d.status === 'submitted' ||
      d.status === 'send_later' ||
      d.status === 'bring_when_boarding' ||
      d.status === 'skipped'
  ).length;
}

/** Map chat-flow / {@code empdoc_forms_to_fill} status strings to checklist progress. */
function normalizeEmpdocFormFillStatus(raw: string | undefined | null): FillingFormProgressStatus {
  const s = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
  if (s === 'completed') return 'completed';
  if (s === 'in_progress') return 'in_progress';
  if (s === 'final_confirm') return 'final_confirm';
  if (s === 'notyet') return 'notyet';
  return 'notyet';
}

function isDoneChecklistStatus(
  status: FillingFormProgressStatus
): status is 'completed' | 'final_confirm' {
  return status === 'completed' || status === 'final_confirm';
}

/** Edit opened from a completed checklist row; cancel mid-edit → Resume (in_progress). */
type FillInChecklistEditSession =
  | { kind: 'generalInfo'; previousStatus: 'completed' | 'final_confirm' }
  | { kind: 'familyInfo'; previousStatus: 'completed' | 'final_confirm' }
  | { kind: 'form'; formCode: string; previousStatus: 'completed' | 'final_confirm' };

const SubmitDocumentsFlow: React.FC<SubmitDocumentsFlowProps> = ({ onBackToMenu, mode = 'submit' }) => {
  const { language, t } = useLanguage();
  const [phase, setPhase] = useState<Phase>('roadmap');
  const [variousCopiesTotal, setVariousCopiesTotal] = useState<number | undefined>(undefined);
  const [variousCopiesCompleted, setVariousCopiesCompleted] = useState<number | undefined>(undefined);
  const [formsToFillTotal, setFormsToFillTotal] = useState<number | undefined>(undefined);
  const [formsToFillCompleted, setFormsToFillCompleted] = useState<number | undefined>(undefined);
  const [formsToFill, setFormsToFill] = useState<FillingFormsChecklistForm[] | undefined>(undefined);
  const [generalInfoStatus, setGeneralInfoStatus] = useState<FillingFormProgressStatus>('notyet');
  const [hasSignature, setHasSignature] = useState(false);
  const [familyInfoStatus, setFamilyInfoStatus] = useState<FillingFormProgressStatus>('notyet');
  /** In-memory until emp_profile API exists */
  const generalProfileNameDraftRef = useRef<GeneralProfileNamePayload | null>(null);
  /** When true, signature screen returns to general profile final confirm instead of checklist. */
  const signatureReturnToGeneralProfileRef = useRef(false);
  /** Which forms checklist to return to after a form chat (記入 vs 確認). */
  const formsChecklistReturnRef = useRef<FormsChecklistPhase>('fillingFormsChecklist');
  /** Set when Edit is tapped on a completed/final_confirm row; cleared on finish or abandoned on cancel. */
  const fillInChecklistEditSessionRef = useRef<FillInChecklistEditSession | null>(null);
  const [formStatuses, setFormStatuses] = useState<Record<string, FillingFormProgressStatus>>({});
  const [invitationId, setInvitationId] = useState<string>('');
  const [submissionDeadline, setSubmissionDeadline] = useState<string>('');
  const [rejectedCopiesCount, setRejectedCopiesCount] = useState<number>(0);
  const [rejectedFormsCount, setRejectedFormsCount] = useState<number>(0);
  const [profileSections, setProfileSections] = useState<ChatFlowProfileSectionItem[]>([]);
  const [careerUpPath, setCareerUpPath] = useState<CareerUpPathValue | null>(null);
  const [pendingNotice, setPendingNotice] = useState<ChatFlowPendingNotice | null>(null);
  const resubmissionNeededCount = rejectedCopiesCount + rejectedFormsCount;
  const [editDoc, setEditDoc] = useState<ChatFlowDocumentItem | null>(null);
  const [editInvitationId, setEditInvitationId] = useState<string>('');
  /** Worker-registry document chat (Option A: fillingForms/chat/workerRegistry). */
  const [workerRegistryChat, setWorkerRegistryChat] = useState<{
    formCode: string;
    documentTitle: string;
  } | null>(null);
  const [skipFillInChat, setSkipFillInChat] = useState<{
    formCode: string;
    documentTitle: string;
  } | null>(null);
  const [employmentContractChat, setEmploymentContractChat] = useState<{
    formCode: string;
    documentTitle: string;
    previewKind:
      | 'employment-contract'
      | 'pledge'
      | 'password-handling'
      | 'personal-seal'
      | 'previous-employment-inquiry';
    messagesPrefix: string;
  } | null>(null);
  const [hireHealthSurveyChat, setHireHealthSurveyChat] = useState<{
    formCode: string;
    documentTitle: string;
  } | null>(null);

  const [bankAccountDetailsChat, setBankAccountDetailsChat] = useState<{
    formCode: string;
    documentTitle: string;
  } | null>(null);

  const [dependentDeductionChat, setDependentDeductionChat] = useState<{
    formCode: string;
    documentTitle: string;
  } | null>(null);

  const [careerHistorySurveyChat, setCareerHistorySurveyChat] = useState<{
    formCode: string;
    documentTitle: string;
  } | null>(null);

  const [preEmploymentEducationChat, setPreEmploymentEducationChat] = useState<{
    formCode: string;
    documentTitle: string;
  } | null>(null);

  const [siteArrivalConfirmationChat, setSiteArrivalConfirmationChat] = useState<{
    formCode: string;
    documentTitle: string;
  } | null>(null);

  const [careerUpConfirmationChat, setCareerUpConfirmationChat] = useState<{
    formCode: string;
    documentTitle: string;
  } | null>(null);

  const [careerUpApplicationChat, setCareerUpApplicationChat] = useState<{
    formCode: string;
    documentTitle: string;
  } | null>(null);

  const [healthInfoZuidoChat, setHealthInfoZuidoChat] = useState<{
    formCode: string;
    documentTitle: string;
  } | null>(null);
  const [resumeHealthInfoZuidoAfterForms312Preview, setResumeHealthInfoZuidoAfterForms312Preview] =
    useState(false);

  type PdfPreviewReviewSession = {
    request: FillInPdfPreviewRequest;
    returnPhase: Phase;
    /** When true, "Continue" on the PDF screen goes to the fill-in checklist and marks this form completed. */
    confirmNavigateToChecklist?: boolean;
    /** When 'read', confirm returns to chat without completing the form (FF009 consent read gate). */
    intent?: 'read' | 'complete';
    /** FF009: show consent PDF one page at a time (3 pages). */
    pagedTotalPages?: number;
  };
  const [pdfPreviewSession, setPdfPreviewSession] = useState<PdfPreviewReviewSession | null>(null);
  const [pdfPreviewBlobUrl, setPdfPreviewBlobUrl] = useState<string | null>(null);
  const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);
  const [pdfPreviewError, setPdfPreviewError] = useState(false);
  /** Set when spouse Section 2 Edit opens Family Info; return to tax chat afterward. */
  const returnToDependentDeductionAfterFamilyInfoRef = useRef(false);
  const pdfPreviewBlobUrlRef = useRef<string | null>(null);
  const pagedConsentReadResolveRef = useRef<(() => void) | null>(null);
  const pagedPreviewLoadedPageRef = useRef(0);
  const [pdfPreviewCurrentPage, setPdfPreviewCurrentPage] = useState(1);

  /** Refresh signature flag when opening the fill-in checklist (avoids stale false after upload). */
  useEffect(() => {
    if (phase !== 'fillingFormsChecklist' && phase !== 'confirmationFormsChecklist' && phase !== 'roadmap') return;
    const email = getEmployeeEmail()?.trim();
    if (!email || !API_BASE_URL) return;
    let cancelled = false;
    fetch(`${API_BASE_URL}/api/general-profile?email=${encodeURIComponent(email)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((p) => {
        if (!cancelled && p) setHasSignature(isSignatureCaptured(p));
      })
      .catch(() => {
        /* ignore */
      });
    return () => {
      cancelled = true;
    };
  }, [phase]);

  const generalInfoComplete =
    generalInfoStatus === 'final_confirm' || generalInfoStatus === 'completed';

  const ensureSignatureForFillInProgress = useCallback(() => {
    if (generalInfoComplete && !hasSignature) {
      setPhase('signatureCapture');
      return false;
    }
    return true;
  }, [generalInfoComplete, hasSignature]);

  const loadChatFlow = useCallback(() => {
    const email = getEmployeeEmail();
    if (!email) {
      setVariousCopiesTotal(undefined);
      setVariousCopiesCompleted(undefined);
      setFormsToFillTotal(undefined);
      setFormsToFillCompleted(undefined);
      setFormsToFill(undefined);
      setInvitationId('');
      setSubmissionDeadline('');
      setGeneralInfoStatus('notyet');
      setFamilyInfoStatus('notyet');
      return;
    }
    let cancelled = false;
    fetchChatFlow(email, language).then((data) => {
      if (cancelled) return;
      if (!data) {
        setVariousCopiesTotal(undefined);
        setVariousCopiesCompleted(undefined);
        setFormsToFillTotal(undefined);
        setFormsToFillCompleted(undefined);
        setFormsToFill(undefined);
        setInvitationId('');
        setSubmissionDeadline('');
        setRejectedCopiesCount(0);
        setRejectedFormsCount(0);
        setProfileSections([]);
        setCareerUpPath(null);
        setGeneralInfoStatus('notyet');
        return;
      }
      setInvitationId(data.invitationId ?? '');
      setSubmissionDeadline(typeof data.deadline === 'string' ? data.deadline.trim() : '');
      setCareerUpPath(data.careerUpPath ?? null);
      setFormsToFillTotal(
        typeof data.formsToFillTotal === 'number' ? data.formsToFillTotal : 0
      );
      setFormsToFillCompleted(
        typeof data.formsToFillCompleted === 'number' ? data.formsToFillCompleted : 0
      );
      setFormsToFill(
        Array.isArray(data.formsToFill)
          ? data.formsToFill
              .filter((f) => f.applicable !== false)
              .map((f) => ({
              formCode: f.formCode,
              name: f.name,
              nameJa: f.nameJa,
              status: normalizeEmpdocFormFillStatus(f.status),
              adminApproved: f.adminApproved,
              adminComment: f.adminComment,
              roundsBySubmission: f.roundsBySubmission,
              choiceRecorded: f.choiceRecorded,
              applicable: f.applicable,
              careerUpRole: f.careerUpRole,
            }))
          : undefined
      );
      const documents = data.documents ?? [];
      const sections = data.profileSections ?? [];
      setProfileSections(sections);
      setVariousCopiesTotal(documents.length);
      setVariousCopiesCompleted(countVariousCopiesCompleted(documents));
      setRejectedCopiesCount(documents.filter((d) => d.adminApproved === false).length);
      const rejectedForms = (data.formsToFill ?? []).filter((f) => f.adminApproved === false).length;
      const rejectedProfile = sections.filter((s) => s.adminApproved === false).length;
      setRejectedFormsCount(rejectedForms + rejectedProfile);
      const notices = data.pendingNotices ?? [];
      setPendingNotice(notices.length > 0 ? notices[0] : null);
    });

    // Load general-info progress (emp_profile) so checklist counts + "Edit" reflect cross-device state.
    if (API_BASE_URL) {
      fetch(`${API_BASE_URL}/api/general-profile?email=${encodeURIComponent(email)}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((p) => {
          if (cancelled || !p) return;
          const status = typeof p.status === 'string' ? p.status : '';
          const step = typeof p.step === 'string' ? p.step : '';
          if (status === 'completed') setGeneralInfoStatus('completed');
          else if (status === 'final_confirm' || step === 'finalConfirm') setGeneralInfoStatus('final_confirm');
          else if (status === 'in_progress') setGeneralInfoStatus('in_progress');
          else setGeneralInfoStatus('notyet');

          setHasSignature(isSignatureCaptured(p));

          const fStatus = typeof p.familyInfoStatus === 'string' ? p.familyInfoStatus : '';
          if (fStatus === 'completed') setFamilyInfoStatus('completed');
          else if (fStatus === 'final_confirm') setFamilyInfoStatus('final_confirm');
          else if (fStatus === 'in_progress') setFamilyInfoStatus('in_progress');
          else setFamilyInfoStatus('notyet');
        })
        .catch(() => {
          // ignore (treat as not started)
        });
    }

    return () => {
      cancelled = true;
    };
  }, [language]);

  const handleDismissInvitationNotice = useCallback(async () => {
    const email = getEmployeeEmail()?.trim();
    const notice = pendingNotice;
    if (!notice || !email) {
      setPendingNotice(null);
      return;
    }
    await postAckChatFlowNotice(notice.id, email);
    setPendingNotice(null);
    loadChatFlow();
  }, [pendingNotice, loadChatFlow]);

  const invitationNoticeModal = pendingNotice ? (
    <InvitationUpdateNoticeModal
      notice={pendingNotice}
      onDismiss={() => void handleDismissInvitationNotice()}
    />
  ) : null;

  /** Checklist form row statuses mirror chat-flow (empdoc_forms_to_fill); server wins on every fetch. */
  useEffect(() => {
    if (!formsToFill?.length) {
      setFormStatuses({});
      return;
    }
    setFormStatuses(() => {
      const next: Record<string, FillingFormProgressStatus> = {};
      for (const f of formsToFill) {
        next[f.formCode] = f.status ?? 'notyet';
      }
      return next;
    });
  }, [formsToFill]);

  const { fillIn: fillInFormsList, confirmation: confirmationFormsList } = useMemo(
    () => partitionFormsByCategory(formsToFill),
    [formsToFill]
  );

  const isFormRowDone = (status: FillingFormProgressStatus | undefined) =>
    status === 'completed' || status === 'final_confirm';

  const fillInFormsTotal = fillInFormsList.length;
  const fillInFormsCompleted = fillInFormsList.filter((f) => isFormRowDone(f.status)).length;
  /** Undefined until chat-flow loads so the roadmap can show 確認書類 during loading / offline. */
  const confirmationFormsTotal =
    formsToFill === undefined ? undefined : confirmationFormsList.length;
  const confirmationFormsCompleted =
    formsToFill === undefined
      ? undefined
      : confirmationFormsList.filter((f) => isFormRowDone(f.status)).length;
  const rejectedFillInBadgeCount =
    fillInFormsList.filter((f) => f.adminApproved === false).length +
    profileSections.filter((s) => s.adminApproved === false).length;
  const rejectedConfirmationBadgeCount = confirmationFormsList.filter(
    (f) => f.adminApproved === false
  ).length;

  const openFillInFormsChecklist = useCallback(() => {
    formsChecklistReturnRef.current = 'fillingFormsChecklist';
    if (generalInfoStatus === 'completed' || generalInfoStatus === 'final_confirm') {
      setPhase('fillingFormsChecklist');
    } else {
      setPhase('fillingFormsIntro');
    }
  }, [generalInfoStatus]);

  const openConfirmationFormsChecklist = useCallback(() => {
    formsChecklistReturnRef.current = 'confirmationFormsChecklist';
    setPhase('confirmationFormsChecklist');
  }, []);

  useEffect(() => {
    pdfPreviewBlobUrlRef.current = pdfPreviewBlobUrl;
  }, [pdfPreviewBlobUrl]);

  useEffect(() => {
    return () => {
      const u = pdfPreviewBlobUrlRef.current;
      if (u) URL.revokeObjectURL(u);
    };
  }, []);

  const dismissPdfPreview = useCallback(() => {
    setPdfPreviewBlobUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setPdfPreviewSession(null);
    setPdfPreviewError(false);
    setPdfPreviewLoading(false);
    pagedPreviewLoadedPageRef.current = 0;
    setPdfPreviewCurrentPage(1);
  }, []);

  const clearChecklistEditSession = useCallback(() => {
    fillInChecklistEditSessionRef.current = null;
  }, []);

  const abandonChecklistEditSession = useCallback(async () => {
    const session = fillInChecklistEditSessionRef.current;
    fillInChecklistEditSessionRef.current = null;
    if (!session) return;

    const email = getEmployeeEmail()?.trim();
    if (session.kind === 'generalInfo') {
      if (email && API_BASE_URL) {
        try {
          await fetch(`${API_BASE_URL}/api/general-profile`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email,
              status: 'in_progress',
              step: 'finalConfirm',
            }),
          });
        } catch {
          /* ignore network */
        }
      }
      setGeneralInfoStatus('in_progress');
      return;
    }

    if (session.kind === 'familyInfo') {
      if (email && API_BASE_URL) {
        try {
          await fetch(`${API_BASE_URL}/api/general-profile`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email,
              familyInfoStatus: 'in_progress',
              familyInfoStep: 'finalConfirm',
            }),
          });
        } catch {
          /* ignore network */
        }
      }
      setFamilyInfoStatus('in_progress');
      return;
    }

    if (session.kind === 'form' && session.formCode) {
      const invId = invitationId?.trim();
      if (email && invId) {
        await postReopenFormToFill(email, invId, session.formCode);
      }
      setFormStatuses((prev) => ({ ...prev, [session.formCode]: 'in_progress' }));
      loadChatFlow();
    }
  }, [invitationId, loadChatFlow]);

  const handleSubmit = useCallback(async () => {
    const email = getEmployeeEmail();
    if (!email || !invitationId) return;
    const ok = await postMarkSubmitted(email, invitationId);
    if (ok) {
      onBackToMenu();
    }
  }, [invitationId, onBackToMenu]);

  const loadPagedPreviewPage = useCallback(
    async (page: number, session: PdfPreviewReviewSession) => {
      if (pagedPreviewLoadedPageRef.current === page) return;
      pagedPreviewLoadedPageRef.current = page;
      setPdfPreviewCurrentPage(page);
      setPdfPreviewLoading(true);
      setPdfPreviewError(false);
      const filledPreview =
        session.request.filledPreview === true ||
        (session.request.kind === 'career-up-application' && session.intent !== 'read');
      try {
        const blob = await fetchFillInPdfPreviewBlob({
          ...session.request,
          page,
          filledPreview,
        });
        setPdfPreviewBlobUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(blob);
        });
      } catch {
        setPdfPreviewError(true);
        // Keep attempted page so PdfPreviewReviewScreen does not retry in a loop on failure.
      } finally {
        setPdfPreviewLoading(false);
      }
    },
    []
  );

  /** In-app full-screen PDF review (reusable model: `fillInPdfPreviewModel` + `PdfPreviewReviewScreen`). */
  const openFillInPdfPreviewReview = useCallback(
    async (ctx: {
      kind: FillInPdfPreviewRequest['kind'];
      documentTitle: string;
      formCode: string;
      returnPhase: Phase;
      confirmNavigateToChecklist?: boolean;
      intent?: 'read' | 'complete';
      pagedTotalPages?: number;
      taxYear?: number;
      filledPreview?: boolean;
      workHistoryFormOnly?: boolean;
      signedPreview?: boolean;
    }) => {
    const email = getEmployeeEmail();
    if (!email?.trim() || !API_BASE_URL) return;

    const isPagedConsentPreview =
      (ctx.kind === 'career-up-application' || ctx.kind === 'health-info-zuido') &&
      typeof ctx.pagedTotalPages === 'number' &&
      ctx.pagedTotalPages > 1 &&
      !ctx.workHistoryFormOnly;

    const request: FillInPdfPreviewRequest = {
      kind: ctx.kind,
      documentTitle: ctx.documentTitle,
      formCode: ctx.formCode,
      email: email.trim(),
      invitationId: invitationId?.trim() || undefined,
      taxYear: ctx.taxYear,
      filledPreview:
        ctx.workHistoryFormOnly === true
          ? true
          : ctx.filledPreview === true
            ? true
            : isPagedConsentPreview
              ? false
              : ctx.filledPreview,
      page: ctx.workHistoryFormOnly ? 3 : isPagedConsentPreview ? 1 : undefined,
      workHistoryFormOnly: ctx.workHistoryFormOnly === true ? true : undefined,
      signedPreview: ctx.signedPreview === true ? true : undefined,
    };

    setPdfPreviewSession({
      request,
      returnPhase: ctx.returnPhase,
      confirmNavigateToChecklist: ctx.confirmNavigateToChecklist ?? true,
      intent: ctx.intent ?? 'complete',
      pagedTotalPages: ctx.pagedTotalPages,
    });
    setPdfPreviewLoading(true);
    setPdfPreviewError(false);
    pagedPreviewLoadedPageRef.current = 0;
    setPdfPreviewCurrentPage(1);
    setPdfPreviewBlobUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setPhase('pdfPreviewReview');

    try {
      if (isPagedConsentPreview) {
        const session: PdfPreviewReviewSession = {
          request,
          returnPhase: ctx.returnPhase,
          confirmNavigateToChecklist: ctx.confirmNavigateToChecklist ?? true,
          intent: ctx.intent ?? 'complete',
          pagedTotalPages: ctx.pagedTotalPages,
        };
        await loadPagedPreviewPage(1, session);
      } else {
        const blob = await fetchFillInPdfPreviewBlob(request);
        setPdfPreviewBlobUrl(URL.createObjectURL(blob));
        setPdfPreviewLoading(false);
      }
    } catch {
      setPdfPreviewError(true);
      setPdfPreviewLoading(false);
    }
  }, [invitationId, loadPagedPreviewPage]);

  const openCareerUpConsentPdfRead = useCallback(() => {
    const session = careerUpApplicationChat;
    if (!session) return Promise.resolve();
    return new Promise<void>((resolve) => {
      pagedConsentReadResolveRef.current = resolve;
      void openFillInPdfPreviewReview({
        kind: 'career-up-application',
        documentTitle: session.documentTitle,
        formCode: session.formCode,
        returnPhase: 'careerUpApplicationChat',
        confirmNavigateToChecklist: false,
        intent: 'read',
        pagedTotalPages: CAREER_UP_CONSENT_PAGE_COUNT,
      });
    });
  }, [careerUpApplicationChat, openFillInPdfPreviewReview]);

  const openHealthInfoZuidoConsentPdfRead = useCallback(() => {
    const session = healthInfoZuidoChat;
    if (!session) return Promise.resolve();
    return new Promise<void>((resolve) => {
      pagedConsentReadResolveRef.current = resolve;
      void openFillInPdfPreviewReview({
        kind: 'health-info-zuido',
        documentTitle: session.documentTitle,
        formCode: session.formCode,
        returnPhase: 'healthInfoZuidoChat',
        confirmNavigateToChecklist: false,
        intent: 'read',
        pagedTotalPages: HEALTH_INFO_ZUIDO_CONSENT_PAGE_COUNT,
      });
    });
  }, [healthInfoZuidoChat, openFillInPdfPreviewReview]);

  const openHealthInfoZuidoFilledPreview = useCallback(() => {
    const session = healthInfoZuidoChat;
    if (!session) return;
    void openFillInPdfPreviewReview({
      kind: 'health-info-zuido',
      documentTitle: session.documentTitle,
      formCode: session.formCode,
      returnPhase: 'healthInfoZuidoChat',
      confirmNavigateToChecklist: false,
      intent: 'complete',
      filledPreview: true,
      pagedTotalPages: HEALTH_INFO_ZUIDO_FILLED_PREVIEW_PAGE_COUNT,
    });
  }, [healthInfoZuidoChat, openFillInPdfPreviewReview]);

  const openHealthInfoZuidoWorkHistoryFormPdf = useCallback(() => {
    const session = healthInfoZuidoChat;
    if (!session) return Promise.resolve();
    return new Promise<void>((resolve) => {
      pagedConsentReadResolveRef.current = resolve;
      void openFillInPdfPreviewReview({
        kind: 'health-info-zuido',
        documentTitle: t('submitDocuments.healthInfoZuido.workHistory.pageTitle'),
        formCode: session.formCode,
        returnPhase: 'healthInfoZuidoChat',
        confirmNavigateToChecklist: false,
        intent: 'read',
        workHistoryFormOnly: true,
      });
    });
  }, [healthInfoZuidoChat, openFillInPdfPreviewReview, t]);

  const handlePdfPreviewBack = useCallback(() => {
    const returnPhase = pdfPreviewSession?.returnPhase ?? 'workerRegistryChat';
    if (pdfPreviewSession?.intent === 'read') {
      pagedConsentReadResolveRef.current = null;
    }
    dismissPdfPreview();
    setPhase(returnPhase);
  }, [pdfPreviewSession, dismissPdfPreview]);

  const handlePdfPreviewConfirm = useCallback(async () => {
    const session = pdfPreviewSession;
    const loadFailed = pdfPreviewError;
    const formCode = session?.request.formCode?.trim();
    const returnPhase = session?.returnPhase ?? 'workerRegistryChat';

    if (!session || !formCode || loadFailed) {
      dismissPdfPreview();
      setPhase(returnPhase);
      return;
    }

    if (session.intent === 'read') {
      dismissPdfPreview();
      setPhase(returnPhase);
      pagedConsentReadResolveRef.current?.();
      pagedConsentReadResolveRef.current = null;
      return;
    }

    if (!session.confirmNavigateToChecklist) {
      dismissPdfPreview();
      if (
        session.returnPhase === 'healthInfoZuidoChat' &&
        session.request.kind === 'health-info-zuido' &&
        session.request.filledPreview &&
        !session.request.workHistoryFormOnly
      ) {
        setResumeHealthInfoZuidoAfterForms312Preview(true);
        setPhase('healthInfoZuidoChat');
        return;
      }
      setPhase(returnPhase);
      return;
    }

    const email = getEmployeeEmail()?.trim();
    const invId = invitationId?.trim();
    if (!email || !invId) {
      dismissPdfPreview();
      window.alert(t('submitDocuments.pdfPreviewReview.completeFailed'));
      setPhase(returnPhase);
      return;
    }

    const ok = await postCompleteFormToFill(email, invId, formCode);
    dismissPdfPreview();
    if (ok) {
      clearChecklistEditSession();
      setFormStatuses((prev) => ({ ...prev, [formCode]: 'completed' }));
      setWorkerRegistryChat(null);
      setHireHealthSurveyChat(null);
      setBankAccountDetailsChat(null);
      setDependentDeductionChat(null);
      setCareerHistorySurveyChat(null);
      setPreEmploymentEducationChat(null);
      setSiteArrivalConfirmationChat(null);
      setCareerUpConfirmationChat(null);
      setCareerUpApplicationChat(null);
      setEmploymentContractChat(null);
      setPhase(formsChecklistReturnRef.current);
      loadChatFlow();
      return;
    }
    window.alert(t('submitDocuments.pdfPreviewReview.completeFailed'));
    setPhase(returnPhase);
  }, [pdfPreviewSession, pdfPreviewError, dismissPdfPreview, invitationId, t, loadChatFlow, clearChecklistEditSession]);

  const handleBeforeStartFormItem = useCallback(
    async (item: FillingFormsChecklistItem) => {
      if (item.kind !== 'form') return true;
      if (!ensureSignatureForFillInProgress()) return false;

      const code = item.formCode.trim().toUpperCase();
      if (code === 'FF008' || code === 'FF009') {
        if (careerUpPath === 'vc008_pending') {
          window.alert(t('submitDocuments.careerUp.completeVc008First'));
          return false;
        }
        if (code === 'FF008' && careerUpPath === 'no_card') {
          window.alert(t('submitDocuments.careerUp.useApplicationForm'));
          return false;
        }
        if (code === 'FF009' && careerUpPath === 'has_card') {
          window.alert(t('submitDocuments.careerUp.useConfirmationForm'));
          return false;
        }
      }

      if (!resolveFillInFormFlowId(item.formCode)) {
        window.alert(t('submitDocuments.fillFormsChecklist.fillInFormFlowNotSupported'));
        return false;
      }
      const email = getEmployeeEmail();
      if (!email?.trim() || !invitationId.trim()) {
        window.alert(t('submitDocuments.fillFormsChecklist.startFormFailed'));
        return false;
      }
      const ok = await postStartFormToFill(email, invitationId, item.formCode);
      if (!ok) {
        window.alert(t('submitDocuments.fillFormsChecklist.startFormFailed'));
      }
      return ok;
    },
    [invitationId, t, ensureSignatureForFillInProgress, careerUpPath]
  );

  const openGeneralInfoEditFromChecklist = useCallback(async () => {
    if (!ensureSignatureForFillInProgress()) return;
    const email = getEmployeeEmail()?.trim();
    const statusToSave: FillingFormProgressStatus =
      generalInfoStatus === 'completed' ? 'completed' : 'final_confirm';
    if (email && API_BASE_URL) {
      try {
        await fetch(`${API_BASE_URL}/api/general-profile`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            status: statusToSave,
            step: 'finalConfirm',
          }),
        });
      } catch {
        /* ignore network */
      }
    }
    setPhase('fillingGeneralProfileName');
  }, [ensureSignatureForFillInProgress, generalInfoStatus]);

  const openFamilyInfoResumeFromChecklist = useCallback(async () => {
    if (!ensureSignatureForFillInProgress()) return;
    const email = getEmployeeEmail()?.trim();
    if (email && API_BASE_URL) {
      try {
        await fetch(`${API_BASE_URL}/api/general-profile`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            familyInfoStatus: 'in_progress',
            familyInfoStep: 'hasMembers',
          }),
        });
      } catch {
        /* ignore network */
      }
    }
    setPhase('fillingFamilyInfo');
  }, [ensureSignatureForFillInProgress]);

  /** Reopen at final review; do not downgrade to in_progress (keeps checklist Edit, not Resume). */
  const openFamilyInfoEditFromChecklist = useCallback(async () => {
    if (!ensureSignatureForFillInProgress()) return;
    const email = getEmployeeEmail()?.trim();
    const statusToSave: FillingFormProgressStatus =
      familyInfoStatus === 'completed' ? 'completed' : 'final_confirm';
    if (email && API_BASE_URL) {
      try {
        await fetch(`${API_BASE_URL}/api/general-profile`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            familyInfoStatus: statusToSave,
            familyInfoStep: 'finalConfirm',
          }),
        });
      } catch {
        /* ignore network */
      }
    }
    setPhase('fillingFamilyInfo');
  }, [ensureSignatureForFillInProgress, familyInfoStatus]);

  /** Spouse Section 2 confirm-only: edit spouse tax fields in Family Info, then return to tax chat. */
  const openFamilyInfoFromDependentDeduction = useCallback(async () => {
    if (!dependentDeductionChat) return;
    returnToDependentDeductionAfterFamilyInfoRef.current = true;
    await openFamilyInfoEditFromChecklist();
  }, [dependentDeductionChat, openFamilyInfoEditFromChecklist]);

  const openFillInFormSessionFromChecklistItem = useCallback((item: FillingFormsChecklistItem) => {
    if (item.kind !== 'form') return;
    formsChecklistReturnRef.current = isConfirmationFormCode(item.formCode)
      ? 'confirmationFormsChecklist'
      : 'fillingFormsChecklist';
    const flow = resolveFillInFormFlowId(item.formCode);
    if (flow === 'worker-registry') {
      setWorkerRegistryChat({ formCode: item.formCode, documentTitle: item.title });
      setPhase('workerRegistryChat');
      return;
    }
    if (flow === 'skip-fill-in') {
      setSkipFillInChat({ formCode: item.formCode, documentTitle: item.title });
      setPhase('skipFillInChat');
      return;
    }
    if (flow === 'employment-contract') {
      setEmploymentContractChat({
        formCode: item.formCode,
        documentTitle: item.title,
        previewKind: 'employment-contract',
        messagesPrefix: 'submitDocuments.employmentContract',
      });
      setPhase('employmentContractChat');
      return;
    }
    if (flow === 'pledge') {
      setEmploymentContractChat({
        formCode: item.formCode,
        documentTitle: item.title,
        previewKind: 'pledge',
        messagesPrefix: 'submitDocuments.pledge',
      });
      setPhase('employmentContractChat');
      return;
    }
    if (flow === 'password-handling') {
      setEmploymentContractChat({
        formCode: item.formCode,
        documentTitle: item.title,
        previewKind: 'password-handling',
        messagesPrefix: 'submitDocuments.passwordHandling',
      });
      setPhase('employmentContractChat');
      return;
    }
    if (flow === 'personal-seal') {
      setEmploymentContractChat({
        formCode: item.formCode,
        documentTitle: item.title,
        previewKind: 'personal-seal',
        messagesPrefix: 'submitDocuments.personalSeal',
      });
      setPhase('employmentContractChat');
      return;
    }
    if (flow === 'previous-employment-inquiry') {
      setEmploymentContractChat({
        formCode: item.formCode,
        documentTitle: item.title,
        previewKind: 'previous-employment-inquiry',
        messagesPrefix: 'submitDocuments.previousEmploymentInquiry',
      });
      setPhase('employmentContractChat');
      return;
    }
    if (flow === 'hire-health-survey') {
      setHireHealthSurveyChat({ formCode: item.formCode, documentTitle: item.title });
      setPhase('hireHealthSurveyChat');
      return;
    }
    if (flow === 'bank-account-details') {
      setBankAccountDetailsChat({ formCode: item.formCode, documentTitle: item.title });
      setPhase('bankAccountDetailsChat');
      return;
    }
    if (flow === 'dependent-deduction-declaration') {
      setDependentDeductionChat({ formCode: item.formCode, documentTitle: item.title });
      setPhase('dependentDeductionChat');
      return;
    }
    if (flow === 'career-history-survey') {
      setCareerHistorySurveyChat({ formCode: item.formCode, documentTitle: item.title });
      setPhase('careerHistorySurveyChat');
      return;
    }
    if (flow === 'pre-employment-education') {
      setPreEmploymentEducationChat({ formCode: item.formCode, documentTitle: item.title });
      setPhase('preEmploymentEducationChat');
      return;
    }
    if (flow === 'site-arrival-confirmation') {
      setSiteArrivalConfirmationChat({ formCode: item.formCode, documentTitle: item.title });
      setPhase('siteArrivalConfirmationChat');
      return;
    }
    if (flow === 'career-up-confirmation') {
      setCareerUpConfirmationChat({ formCode: item.formCode, documentTitle: item.title });
      setPhase('careerUpConfirmationChat');
      return;
    }
    if (flow === 'career-up-application') {
      setCareerUpApplicationChat({ formCode: item.formCode, documentTitle: item.title });
      setPhase('careerUpApplicationChat');
      return;
    }
    if (flow === 'health-info-zuido') {
      setHealthInfoZuidoChat({ formCode: item.formCode, documentTitle: item.title });
      setPhase('healthInfoZuidoChat');
    }
  }, []);

  const completeBankAccountDetailsDeclinedFromChecklist = useCallback(async () => {
    const session = bankAccountDetailsChat;
    if (!session) return;
    const email = getEmployeeEmail()?.trim();
    const invId = invitationId?.trim();
    if (!email || !invId) {
      window.alert(t('submitDocuments.bankAccountDetails.completeFailed'));
      return;
    }
    const ok = await postCompleteFormToFill(email, invId, session.formCode);
    if (!ok) {
      window.alert(t('submitDocuments.bankAccountDetails.completeFailed'));
      return;
    }
    clearChecklistEditSession();
    setFormStatuses((prev) => ({ ...prev, [session.formCode]: 'completed' }));
    setBankAccountDetailsChat(null);
    setPhase(formsChecklistReturnRef.current);
    loadChatFlow();
  }, [bankAccountDetailsChat, invitationId, t, clearChecklistEditSession, loadChatFlow]);

  const completePreEmploymentEducationFromChecklist = useCallback(async () => {
    const session = preEmploymentEducationChat;
    if (!session) return;
    const email = getEmployeeEmail()?.trim();
    const invId = invitationId?.trim();
    if (!email || !invId) {
      window.alert(t('submitDocuments.preEmploymentEducation.completeFailed'));
      return;
    }
    const ok = await postCompleteFormToFill(email, invId, session.formCode);
    if (!ok) {
      window.alert(t('submitDocuments.preEmploymentEducation.completeFailed'));
      return;
    }
    clearChecklistEditSession();
    setFormStatuses((prev) => ({ ...prev, [session.formCode]: 'completed' }));
    setPreEmploymentEducationChat(null);
    setPhase(formsChecklistReturnRef.current);
    loadChatFlow();
  }, [preEmploymentEducationChat, invitationId, t, clearChecklistEditSession, loadChatFlow]);

  const completeCareerUpConfirmationFromChecklist = useCallback(async () => {
    const session = careerUpConfirmationChat;
    if (!session) return;
    const email = getEmployeeEmail()?.trim();
    const invId = invitationId?.trim();
    if (!email || !invId) {
      window.alert(t('submitDocuments.careerUpConfirmation.completeFailed'));
      return;
    }
    const ok = await postCompleteFormToFill(email, invId, session.formCode);
    if (!ok) {
      window.alert(t('submitDocuments.careerUpConfirmation.completeFailed'));
      return;
    }
    clearChecklistEditSession();
    setFormStatuses((prev) => ({ ...prev, [session.formCode]: 'completed' }));
    setCareerUpConfirmationChat(null);
    setPhase(formsChecklistReturnRef.current);
    loadChatFlow();
  }, [careerUpConfirmationChat, invitationId, t, clearChecklistEditSession, loadChatFlow]);

  const returnFromCareerUpConfirmationDeclined = useCallback(() => {
    clearChecklistEditSession();
    setCareerUpConfirmationChat(null);
    setPhase(formsChecklistReturnRef.current);
    loadChatFlow();
  }, [clearChecklistEditSession, loadChatFlow]);

  const completeCareerUpApplicationFromChecklist = useCallback(async () => {
    const session = careerUpApplicationChat;
    if (!session) return;
    const email = getEmployeeEmail()?.trim();
    const invId = invitationId?.trim();
    if (!email || !invId) {
      window.alert(t('submitDocuments.careerUpApplication.completeFailed'));
      return;
    }
    const ok = await postCompleteFormToFill(email, invId, session.formCode);
    if (!ok) {
      window.alert(t('submitDocuments.careerUpApplication.completeFailed'));
      return;
    }
    clearChecklistEditSession();
    setFormStatuses((prev) => ({ ...prev, [session.formCode]: 'completed' }));
    setCareerUpApplicationChat(null);
    setPhase(formsChecklistReturnRef.current);
    loadChatFlow();
  }, [careerUpApplicationChat, invitationId, t, clearChecklistEditSession, loadChatFlow]);

  const returnFromCareerUpApplicationDeclined = useCallback(() => {
    clearChecklistEditSession();
    setCareerUpApplicationChat(null);
    setPhase(formsChecklistReturnRef.current);
    loadChatFlow();
  }, [clearChecklistEditSession, loadChatFlow]);

  const returnFromHealthInfoZuidoDeclined = useCallback(async () => {
    const session = healthInfoZuidoChat;
    if (!session) return;
    const email = getEmployeeEmail()?.trim();
    const invId = invitationId?.trim();
    if (!email || !invId) {
      setHealthInfoZuidoChat(null);
      setPhase(formsChecklistReturnRef.current);
      return;
    }
    const ok = await postCompleteFormToFill(email, invId, session.formCode);
    if (!ok) {
      window.alert(t('submitDocuments.healthInfoZuido.completeFailed'));
      return;
    }
    clearChecklistEditSession();
    setFormStatuses((prev) => ({ ...prev, [session.formCode]: 'completed' }));
    setHealthInfoZuidoChat(null);
    setPhase(formsChecklistReturnRef.current);
    loadChatFlow();
  }, [healthInfoZuidoChat, invitationId, t, clearChecklistEditSession, loadChatFlow]);

  const completeHealthInfoZuidoFromChecklist = useCallback(async () => {
    const session = healthInfoZuidoChat;
    if (!session) return;
    const email = getEmployeeEmail()?.trim();
    const invId = invitationId?.trim();
    if (!email || !invId) {
      setHealthInfoZuidoChat(null);
      setPhase(formsChecklistReturnRef.current);
      return;
    }
    const ok = await postCompleteFormToFill(email, invId, session.formCode);
    if (!ok) {
      window.alert(t('submitDocuments.healthInfoZuido.completeFailed'));
      return;
    }
    clearChecklistEditSession();
    setFormStatuses((prev) => ({ ...prev, [session.formCode]: 'completed' }));
    setHealthInfoZuidoChat(null);
    setResumeHealthInfoZuidoAfterForms312Preview(false);
    setPhase(formsChecklistReturnRef.current);
    loadChatFlow();
  }, [healthInfoZuidoChat, invitationId, t, clearChecklistEditSession, loadChatFlow]);

  const completeSkipFillInFromChecklist = useCallback(async () => {
    const session = skipFillInChat;
    if (!session) return;
    const email = getEmployeeEmail()?.trim();
    const invId = invitationId?.trim();
    if (!email || !invId) {
      window.alert(t('submitDocuments.skipFillInFormChat.completeFailed'));
      return;
    }
    const ok = await postCompleteFormToFill(email, invId, session.formCode);
    if (!ok) {
      window.alert(t('submitDocuments.skipFillInFormChat.completeFailed'));
      return;
    }
    clearChecklistEditSession();
    setFormStatuses((prev) => ({ ...prev, [session.formCode]: 'completed' }));
    setSkipFillInChat(null);
    setPhase(formsChecklistReturnRef.current);
    loadChatFlow();
  }, [skipFillInChat, invitationId, t, clearChecklistEditSession, loadChatFlow]);

  const returnFromEmploymentContractDeclined = useCallback(() => {
    clearChecklistEditSession();
    setEmploymentContractChat(null);
    setPhase(formsChecklistReturnRef.current);
    loadChatFlow();
  }, [clearChecklistEditSession, loadChatFlow]);

  const handleChecklistEdit = useCallback(
    async (item: FillingFormsChecklistItem) => {
      if (isDoneChecklistStatus(item.status)) {
        if (item.kind === 'generalInfo') {
          fillInChecklistEditSessionRef.current = { kind: 'generalInfo', previousStatus: item.status };
        } else if (item.kind === 'familyInfo') {
          fillInChecklistEditSessionRef.current = { kind: 'familyInfo', previousStatus: item.status };
        } else {
          fillInChecklistEditSessionRef.current = {
            kind: 'form',
            formCode: item.formCode,
            previousStatus: item.status,
          };
        }
      } else {
        fillInChecklistEditSessionRef.current = null;
      }

      if (item.kind === 'generalInfo') {
        await openGeneralInfoEditFromChecklist();
        return;
      }
      if (item.kind === 'familyInfo') {
        await openFamilyInfoEditFromChecklist();
        return;
      }
      if (!ensureSignatureForFillInProgress()) return;
      if (!resolveFillInFormFlowId(item.formCode)) {
        window.alert(t('submitDocuments.fillFormsChecklist.fillInFormFlowNotSupported'));
        return;
      }
      openFillInFormSessionFromChecklistItem(item);
    },
    [
      openGeneralInfoEditFromChecklist,
      openFamilyInfoEditFromChecklist,
      ensureSignatureForFillInProgress,
      t,
      openFillInFormSessionFromChecklistItem,
    ]
  );

  const handleFormReviewFix = useCallback(
    async (item: FillingFormsChecklistItem) => {
      if (item.kind === 'generalInfo' || item.kind === 'familyInfo') {
        await handleChecklistEdit(item);
        return;
      }
      if (item.kind !== 'form') return;
      if (!ensureSignatureForFillInProgress()) return;
      if (!resolveFillInFormFlowId(item.formCode)) {
        window.alert(t('submitDocuments.fillFormsChecklist.fillInFormFlowNotSupported'));
        return;
      }

      if (item.status === 'in_progress' || item.status === 'notyet') {
        const ok = await handleBeforeStartFormItem(item);
        if (!ok) return;
        setFormStatuses((prev) => ({ ...prev, [item.formCode]: 'in_progress' }));
        openFillInFormSessionFromChecklistItem(item);
        return;
      }

      const email = getEmployeeEmail()?.trim();
      const invId = invitationId?.trim();
      if (email && invId) {
        const reopened = await postReopenFormToFill(email, invId, item.formCode);
        if (!reopened) {
          window.alert(t('submitDocuments.fillFormsChecklist.startFormFailed'));
          return;
        }
        setFormStatuses((prev) => ({ ...prev, [item.formCode]: 'in_progress' }));
      }
      openFillInFormSessionFromChecklistItem(item);
    },
    [
      handleChecklistEdit,
      ensureSignatureForFillInProgress,
      t,
      handleBeforeStartFormItem,
      openFillInFormSessionFromChecklistItem,
      invitationId,
    ]
  );

  const completeFamilyInfoFromChecklist = useCallback(async () => {
    const email = getEmployeeEmail()?.trim();
    if (email && API_BASE_URL) {
      try {
        const res = await fetch(`${API_BASE_URL}/api/general-profile`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            familyInfoStatus: 'completed',
            familyInfoStep: 'hasMembers',
          }),
        });
        if (!res.ok) {
          window.alert(t('submitDocuments.familyInfo.saveFailed'));
          return;
        }
      } catch {
        window.alert(t('submitDocuments.familyInfo.saveFailed'));
        return;
      }
    }
    setFamilyInfoStatus('completed');
    setPhase(formsChecklistReturnRef.current);
    loadChatFlow();
  }, [loadChatFlow, t]);

  const handleFillInFormOpenFromChecklist = useCallback(
    (item: FillingFormsChecklistItem) => {
      openFillInFormSessionFromChecklistItem(item);
    },
    [openFillInFormSessionFromChecklistItem]
  );

  /** Edit flow: allow 送信 only when no document is still rejected (all re-uploaded / pending admin). */
  const canSubmitResubmission =
    mode === 'edit' && Boolean(invitationId) && resubmissionNeededCount === 0;

  // When on roadmap, keep progress fresh (e.g. after returning from summary or cancel)
  useEffect(() => {
    if (phase !== 'roadmap') return;
    loadChatFlow();
  }, [phase, loadChatFlow]);

  const handleOpenVariousCopies = useCallback(async () => {
    const email = getEmployeeEmail();
    if (email) {
      await postMarkDraft(email);
    }
    if (mode === 'edit') {
      setPhase('summary');
      return;
    }
    const total = variousCopiesTotal ?? 0;
    const completed = variousCopiesCompleted ?? 0;
    if (total > 0 && completed >= total) {
      setPhase('summary');
    } else {
      setPhase('chat');
    }
  }, [mode, variousCopiesTotal, variousCopiesCompleted]);

  if (phase === 'chat') {
    return (
      <>
        {invitationNoticeModal}
        <ChatDocumentFlow
          onBack={() => setPhase('roadmap')}
          onContinue={() => setPhase('summary')}
        />
      </>
    );
  }

  if (phase === 'summary') {
    return (
      <DocumentSummaryScreen
        mode={mode === 'edit' ? 'review' : 'choices'}
        onBack={() => setPhase(mode === 'edit' ? 'roadmap' : 'chat')}
        onContinue={() => {
          setPhase('roadmap');
          loadChatFlow();
        }}
        onEdit={(doc, invitationId) => {
          setEditDoc(doc);
          setEditInvitationId(invitationId);
          setPhase('edit');
        }}
      />
    );
  }

  if (phase === 'edit' && editDoc && editInvitationId) {
    return (
      <EditDocumentChatFlow
        doc={editDoc}
        invitationId={editInvitationId}
        onBack={() => {
          setPhase('summary');
          setEditDoc(null);
          setEditInvitationId('');
        }}
        onComplete={() => {
          setPhase('summary');
          setEditDoc(null);
          setEditInvitationId('');
          loadChatFlow();
        }}
      />
    );
  }

  if (phase === 'fillingFormsIntro') {
    return (
      <FillingFormsIntroChatFlow
        onBack={() => setPhase('roadmap')}
        onContinue={() => {
          formsChecklistReturnRef.current = 'fillingFormsChecklist';
          setPhase('fillingFormsChecklist');
        }}
      />
    );
  }

  if (phase === 'fillingGeneralProfileName') {
    return (
      <GeneralProfileChatFlow
        currentEmail={getEmployeeEmail()}
        initialValues={generalProfileNameDraftRef.current}
        hasSignature={hasSignature}
        onRedoSignature={() => {
          signatureReturnToGeneralProfileRef.current = true;
          setPhase('signatureCapture');
        }}
        onBack={() => {
          void abandonChecklistEditSession();
          if (hasSignature) setPhase(formsChecklistReturnRef.current);
          else setPhase('roadmap');
        }}
        onProceedToSignature={(data) => {
          generalProfileNameDraftRef.current = data;
          setGeneralInfoStatus('final_confirm');
          signatureReturnToGeneralProfileRef.current = true;
          setPhase('signatureCapture');
        }}
        onComplete={(data) => {
          clearChecklistEditSession();
          generalProfileNameDraftRef.current = data;
          setGeneralInfoStatus('completed');
          setPhase(formsChecklistReturnRef.current);
        }}
      />
    );
  }

  if (phase === 'signatureCapture') {
    const email = getEmployeeEmail();
    if (!email?.trim()) {
      return null;
    }
    return (
      <SignatureCaptureScreen
        email={email}
        onBack={() => {
          if (signatureReturnToGeneralProfileRef.current) {
            setPhase('fillingGeneralProfileName');
            return;
          }
          if (generalInfoComplete) setPhase(formsChecklistReturnRef.current);
          else setPhase('fillingGeneralProfileName');
        }}
        onComplete={() => {
          setHasSignature(true);
          if (signatureReturnToGeneralProfileRef.current) {
            signatureReturnToGeneralProfileRef.current = false;
            setPhase('fillingGeneralProfileName');
          } else {
            setPhase(formsChecklistReturnRef.current);
          }
        }}
      />
    );
  }

  if (phase === 'fillingFamilyInfo') {
    return (
      <FamilyInfoChatFlow
        currentEmail={getEmployeeEmail()}
        invitationId={invitationId || undefined}
        onBack={() => {
          if (returnToDependentDeductionAfterFamilyInfoRef.current) {
            returnToDependentDeductionAfterFamilyInfoRef.current = false;
            setPhase('dependentDeductionChat');
            return;
          }
          void abandonChecklistEditSession();
          setPhase(formsChecklistReturnRef.current);
        }}
        onComplete={() => {
          if (returnToDependentDeductionAfterFamilyInfoRef.current) {
            returnToDependentDeductionAfterFamilyInfoRef.current = false;
            setFamilyInfoStatus('completed');
            setPhase('dependentDeductionChat');
            loadChatFlow();
            return;
          }
          clearChecklistEditSession();
          setFamilyInfoStatus('completed');
          setPhase(formsChecklistReturnRef.current);
          loadChatFlow();
        }}
      />
    );
  }

  if (phase === 'pdfPreviewReview' && pdfPreviewSession) {
    const isReadIntent = pdfPreviewSession.intent === 'read';
    const isPagedConsentPreview = isPagedConsentPreviewSession(pdfPreviewSession);
    const previewKind = pdfPreviewSession.request.kind;
    const pagedPageTitleKey = isPagedConsentPreview
      ? pagedPreviewPageTitleKey(previewKind, pdfPreviewCurrentPage)
      : null;
    const pagedPageTitle = pagedPageTitleKey ? t(pagedPageTitleKey) : null;
    return (
      <PdfPreviewReviewScreen
        screenTitle={t('submitDocuments.pdfPreviewReview.screenTitle')}
        documentTitle={
          isPagedConsentPreview && pagedPageTitle ? pagedPageTitle : pdfPreviewSession.request.documentTitle
        }
        helperText={
          isReadIntent
            ? pdfPreviewSession.request.workHistoryFormOnly
              ? t('submitDocuments.healthInfoZuido.workHistory.formHelper')
              : t(pagedPreviewReadHelperKey(previewKind, pdfPreviewSession.request.filledPreview))
            : pdfPreviewSession.request.filledPreview
              ? t('submitDocuments.healthInfoZuido.filledPreviewHelper')
              : t('submitDocuments.pdfPreviewReview.helper')
        }
        pdfUrl={pdfPreviewBlobUrl}
        loading={pdfPreviewLoading}
        error={pdfPreviewError}
        loadingLabel={t('submitDocuments.pdfPreviewReview.loading')}
        errorLabel={t('submitDocuments.pdfPreviewReview.loadError')}
        backLabel={t('submitDocuments.pdfPreviewReview.backToChat')}
        confirmLabel={
          isReadIntent
            ? pdfPreviewSession.request.workHistoryFormOnly
              ? t('submitDocuments.chat.continue')
              : t(pagedPreviewReadConfirmKey(previewKind))
            : t('submitDocuments.pdfPreviewReview.continue')
        }
        pagedTotalPages={isPagedConsentPreview ? pdfPreviewSession.pagedTotalPages : undefined}
        pagedSeparateDocuments={isPagedConsentPreview}
        onPageChange={
          isPagedConsentPreview
            ? (page) => {
                void loadPagedPreviewPage(page, pdfPreviewSession);
              }
            : undefined
        }
        pageIndicatorLabel={t(pagedPreviewPageIndicatorKey(previewKind))}
        nextPageLabel={t(pagedPreviewNextPageKey(previewKind))}
        prevPageLabel={t(pagedPreviewPrevPageKey(previewKind))}
        onBack={handlePdfPreviewBack}
        onConfirm={handlePdfPreviewConfirm}
        openInNewTabLabel={t('submitDocuments.pdfPreviewReview.openInNewTab')}
        onOpenInNewTab={
          pdfPreviewBlobUrl
            ? () => {
                window.open(pdfPreviewBlobUrl, '_blank', 'noopener,noreferrer');
              }
            : undefined
        }
      />
    );
  }

  if (phase === 'skipFillInChat' && skipFillInChat) {
    return (
      <SkipFillInFormChatFlow
        documentTitle={skipFillInChat.documentTitle}
        onBack={() => {
          void abandonChecklistEditSession();
          setSkipFillInChat(null);
          setPhase(formsChecklistReturnRef.current);
        }}
        onContinue={() => void completeSkipFillInFromChecklist()}
      />
    );
  }

  if (phase === 'employmentContractChat' && employmentContractChat) {
    return (
      <EmploymentContractChatFlow
        documentTitle={employmentContractChat.documentTitle}
        formCode={employmentContractChat.formCode}
        messagesPrefix={employmentContractChat.messagesPrefix}
        currentEmail={getEmployeeEmail()}
        invitationId={invitationId}
        onBack={() => {
          void abandonChecklistEditSession();
          setEmploymentContractChat(null);
          setPhase(formsChecklistReturnRef.current);
        }}
        onOpenUnsignedPreview={() =>
          void openFillInPdfPreviewReview({
            kind: employmentContractChat.previewKind,
            documentTitle: employmentContractChat.documentTitle,
            formCode: employmentContractChat.formCode,
            returnPhase: 'employmentContractChat',
            confirmNavigateToChecklist: false,
            intent: 'read',
            signedPreview: false,
          })
        }
        onOpenSignedPreview={() =>
          void openFillInPdfPreviewReview({
            kind: employmentContractChat.previewKind,
            documentTitle: employmentContractChat.documentTitle,
            formCode: employmentContractChat.formCode,
            returnPhase: 'employmentContractChat',
            confirmNavigateToChecklist: true,
            intent: 'complete',
            signedPreview: true,
          })
        }
        onDeclinedContinue={() => returnFromEmploymentContractDeclined()}
      />
    );
  }

  if (phase === 'workerRegistryChat' && workerRegistryChat) {
    return (
      <WorkerRegistryChatFlow
        formCode={workerRegistryChat.formCode}
        documentTitle={workerRegistryChat.documentTitle}
        currentEmail={getEmployeeEmail()}
        onBack={() => {
          void abandonChecklistEditSession();
          setWorkerRegistryChat(null);
          setPhase(formsChecklistReturnRef.current);
        }}
        onOpenPdfPreview={() =>
          void openFillInPdfPreviewReview({
            kind: 'worker-registry',
            documentTitle: workerRegistryChat.documentTitle,
            formCode: workerRegistryChat.formCode,
            returnPhase: 'workerRegistryChat',
          })
        }
      />
    );
  }

  if (phase === 'hireHealthSurveyChat' && hireHealthSurveyChat) {
    return (
      <HireHealthSurveyChatFlow
        documentTitle={hireHealthSurveyChat.documentTitle}
        currentEmail={getEmployeeEmail()}
        onBack={() => {
          void abandonChecklistEditSession();
          setHireHealthSurveyChat(null);
          setPhase(formsChecklistReturnRef.current);
        }}
        onOpenPdfPreview={() =>
          void openFillInPdfPreviewReview({
            kind: 'hire-health-survey',
            documentTitle: hireHealthSurveyChat.documentTitle,
            formCode: hireHealthSurveyChat.formCode,
            returnPhase: 'hireHealthSurveyChat',
          })
        }
      />
    );
  }

  if (phase === 'bankAccountDetailsChat' && bankAccountDetailsChat) {
    return (
      <BankAccountDetailsChatFlow
        documentTitle={bankAccountDetailsChat.documentTitle}
        currentEmail={getEmployeeEmail()}
        onBack={() => {
          void abandonChecklistEditSession();
          setBankAccountDetailsChat(null);
          setPhase(formsChecklistReturnRef.current);
        }}
        onOpenPdfPreview={() =>
          void openFillInPdfPreviewReview({
            kind: 'bank-account-details',
            documentTitle: bankAccountDetailsChat.documentTitle,
            formCode: bankAccountDetailsChat.formCode,
            returnPhase: 'bankAccountDetailsChat',
          })
        }
        onConsentDeclinedContinue={() => void completeBankAccountDetailsDeclinedFromChecklist()}
      />
    );
  }

  if (phase === 'dependentDeductionChat' && dependentDeductionChat) {
    return (
      <DependentDeductionChatFlow
        documentTitle={dependentDeductionChat.documentTitle}
        onOpenFamilyInfo={() => void openFamilyInfoFromDependentDeduction()}
        onOpenPdfPreview={() =>
          void openFillInPdfPreviewReview({
            kind: 'tax-declaration',
            documentTitle: dependentDeductionChat.documentTitle,
            formCode: dependentDeductionChat.formCode,
            taxYear: new Date().getFullYear(),
            returnPhase: 'dependentDeductionChat',
          })
        }
        onBack={() => {
          void abandonChecklistEditSession();
          returnToDependentDeductionAfterFamilyInfoRef.current = false;
          setDependentDeductionChat(null);
          setPhase(formsChecklistReturnRef.current);
        }}
      />
    );
  }

  if (phase === 'careerHistorySurveyChat' && careerHistorySurveyChat) {
    return (
      <CareerHistorySurveyChatFlow
        documentTitle={careerHistorySurveyChat.documentTitle}
        currentEmail={getEmployeeEmail()}
        invitationId={invitationId || undefined}
        onOpenPdfPreview={() =>
          void openFillInPdfPreviewReview({
            kind: 'work-dust-history',
            documentTitle: careerHistorySurveyChat.documentTitle,
            formCode: careerHistorySurveyChat.formCode,
            returnPhase: 'careerHistorySurveyChat',
          })
        }
        onBack={() => {
          void abandonChecklistEditSession();
          setCareerHistorySurveyChat(null);
          setPhase(formsChecklistReturnRef.current);
        }}
      />
    );
  }

  if (phase === 'preEmploymentEducationChat' && preEmploymentEducationChat) {
    return (
      <PreEmploymentEducationChatFlow
        documentTitle={preEmploymentEducationChat.documentTitle}
        currentEmail={getEmployeeEmail()}
        invitationId={invitationId || undefined}
        onBack={() => {
          void abandonChecklistEditSession();
          setPreEmploymentEducationChat(null);
          setPhase(formsChecklistReturnRef.current);
        }}
        onOpenPdfPreview={() =>
          void openFillInPdfPreviewReview({
            kind: 'pre-employment-education',
            documentTitle: preEmploymentEducationChat.documentTitle,
            formCode: preEmploymentEducationChat.formCode,
            returnPhase: 'preEmploymentEducationChat',
          })
        }
        onCompleteContinue={() => void completePreEmploymentEducationFromChecklist()}
        onDeclinedContinue={() => void completePreEmploymentEducationFromChecklist()}
      />
    );
  }

  if (phase === 'siteArrivalConfirmationChat' && siteArrivalConfirmationChat) {
    return (
      <SiteArrivalConfirmationChatFlow
        documentTitle={siteArrivalConfirmationChat.documentTitle}
        currentEmail={getEmployeeEmail()}
        invitationId={invitationId || undefined}
        onBack={() => {
          void abandonChecklistEditSession();
          setSiteArrivalConfirmationChat(null);
          setPhase(formsChecklistReturnRef.current);
        }}
        onOpenPdfPreview={() =>
          void openFillInPdfPreviewReview({
            kind: 'site-arrival-confirmation',
            documentTitle: siteArrivalConfirmationChat.documentTitle,
            formCode: siteArrivalConfirmationChat.formCode,
            returnPhase: 'siteArrivalConfirmationChat',
          })
        }
      />
    );
  }

  if (phase === 'careerUpConfirmationChat' && careerUpConfirmationChat) {
    return (
      <CareerUpConfirmationChatFlow
        documentTitle={careerUpConfirmationChat.documentTitle}
        currentEmail={getEmployeeEmail()}
        invitationId={invitationId || undefined}
        onBack={() => {
          void abandonChecklistEditSession();
          setCareerUpConfirmationChat(null);
          setPhase(formsChecklistReturnRef.current);
        }}
        onCompleteContinue={() => void completeCareerUpConfirmationFromChecklist()}
        onNoCardContinue={() => returnFromCareerUpConfirmationDeclined()}
        onUploadNeededContinue={() => returnFromCareerUpConfirmationDeclined()}
      />
    );
  }

  if (phase === 'careerUpApplicationChat' && careerUpApplicationChat) {
    return (
      <CareerUpApplicationChatFlow
        documentTitle={careerUpApplicationChat.documentTitle}
        currentEmail={getEmployeeEmail()}
        invitationId={invitationId || undefined}
        onBack={() => {
          void abandonChecklistEditSession();
          setCareerUpApplicationChat(null);
          setPhase(formsChecklistReturnRef.current);
        }}
        onOpenConsentPdfRead={openCareerUpConsentPdfRead}
        onOpenPdfPreview={() =>
          void openFillInPdfPreviewReview({
            kind: 'career-up-application',
            documentTitle: careerUpApplicationChat.documentTitle,
            formCode: careerUpApplicationChat.formCode,
            returnPhase: 'careerUpApplicationChat',
            pagedTotalPages: CAREER_UP_CONSENT_PAGE_COUNT,
          })
        }
        onCompleteContinue={() => void completeCareerUpApplicationFromChecklist()}
        onDeclinedContinue={() => returnFromCareerUpApplicationDeclined()}
      />
    );
  }

  if (phase === 'healthInfoZuidoChat' && healthInfoZuidoChat) {
    return (
      <HealthInfoZuidoChatFlow
        documentTitle={healthInfoZuidoChat.documentTitle}
        currentEmail={getEmployeeEmail()}
        invitationId={invitationId || undefined}
        onBack={() => {
          void abandonChecklistEditSession();
          setHealthInfoZuidoChat(null);
          setResumeHealthInfoZuidoAfterForms312Preview(false);
          setPhase(formsChecklistReturnRef.current);
        }}
        onPauseContinue={() => openHealthInfoZuidoFilledPreview()}
        onDeclinedContinue={() => void returnFromHealthInfoZuidoDeclined()}
        onOpenConsentPdfRead={openHealthInfoZuidoConsentPdfRead}
        onOpenWorkHistoryFormPdf={openHealthInfoZuidoWorkHistoryFormPdf}
        onWorkHistoryCompleteContinue={() => void completeHealthInfoZuidoFromChecklist()}
        resumeAfterForms312Preview={resumeHealthInfoZuidoAfterForms312Preview}
        onForms312PreviewResumed={() => setResumeHealthInfoZuidoAfterForms312Preview(false)}
      />
    );
  }

  if (phase === 'fillingFormsChecklist') {
    return (
      <FillingFormsChecklistScreen
        mode={mode === 'edit' ? 'review' : 'choices'}
        section="fill-in"
        onBack={() => setPhase('roadmap')}
        forms={fillInFormsList}
        formsCountHint={fillInFormsTotal}
        generalInfoStatus={generalInfoStatus}
        onGeneralInfoStatusChange={setGeneralInfoStatus}
        familyInfoStatus={familyInfoStatus}
        onFamilyInfoStatusChange={setFamilyInfoStatus}
        formStatuses={formStatuses}
        onFormStatusChange={(formCode, status) =>
          setFormStatuses((prev) => ({ ...prev, [formCode]: status }))
        }
        onOpenGeneralInfoChat={() => setPhase('fillingGeneralProfileName')}
        onOpenFamilyInfoChat={() => void openFamilyInfoResumeFromChecklist()}
        onEditChecklistItem={handleChecklistEdit}
        onFixRejectedForm={handleFormReviewFix}
        profileSections={profileSections}
        onBeforeStartFormItem={handleBeforeStartFormItem}
        onStartItem={handleFillInFormOpenFromChecklist}
      />
    );
  }

  if (phase === 'confirmationFormsChecklist') {
    return (
      <FillingFormsChecklistScreen
        mode={mode === 'edit' ? 'review' : 'choices'}
        section="confirmation"
        onBack={() => setPhase('roadmap')}
        forms={confirmationFormsList}
        formsCountHint={confirmationFormsTotal}
        generalInfoStatus={generalInfoStatus}
        onGeneralInfoStatusChange={setGeneralInfoStatus}
        familyInfoStatus={familyInfoStatus}
        onFamilyInfoStatusChange={setFamilyInfoStatus}
        formStatuses={formStatuses}
        onFormStatusChange={(formCode, status) =>
          setFormStatuses((prev) => ({ ...prev, [formCode]: status }))
        }
        onOpenGeneralInfoChat={() => setPhase('fillingGeneralProfileName')}
        onOpenFamilyInfoChat={() => void openFamilyInfoResumeFromChecklist()}
        onEditChecklistItem={handleChecklistEdit}
        onFixRejectedForm={handleFormReviewFix}
        profileSections={profileSections}
        onBeforeStartFormItem={handleBeforeStartFormItem}
        onStartItem={handleFillInFormOpenFromChecklist}
      />
    );
  }

  return (
    <>
      {invitationNoticeModal}
      <DocumentChecklistScreen
        onBack={onBackToMenu}
        onSubmit={mode === 'submit' ? handleSubmit : canSubmitResubmission ? handleSubmit : undefined}
        onOpenVariousCopies={handleOpenVariousCopies}
        onOpenFillingForms={openFillInFormsChecklist}
        onOpenConfirmationForms={openConfirmationFormsChecklist}
        variousCopiesTotal={variousCopiesTotal}
        variousCopiesCompleted={variousCopiesCompleted}
        formsToFillTotal={fillInFormsTotal}
        formsToFillCompleted={fillInFormsCompleted}
        confirmationFormsTotal={confirmationFormsTotal}
        confirmationFormsCompleted={confirmationFormsCompleted}
        generalInfoStatus={generalInfoStatus}
        familyInfoStatus={familyInfoStatus}
        submissionDeadline={submissionDeadline}
        copiesBadgeCount={mode === 'edit' && rejectedCopiesCount > 0 ? rejectedCopiesCount : undefined}
        fillInBadgeCount={
          mode === 'edit' && rejectedFillInBadgeCount > 0 ? rejectedFillInBadgeCount : undefined
        }
        confirmationBadgeCount={
          mode === 'edit' && rejectedConfirmationBadgeCount > 0
            ? rejectedConfirmationBadgeCount
            : undefined
        }
      />
    </>
  );
};

export default SubmitDocumentsFlow;
